/** Chord symbols -> pitch classes, and chord charts -> one chord per step. Pure. */

import { pitchClass, placeAbove } from '../notes';
import { STEPS_PER_BAR } from './types';

/** Semitones above the root, by chord quality. */
const QUALITY: Readonly<Record<string, readonly number[]>> = {
  '': [0, 4, 7],
  m: [0, 3, 7],
  '7': [0, 4, 7, 10],
  m7: [0, 3, 7, 10],
  maj7: [0, 4, 7, 11],
  '6': [0, 4, 7, 9],
  m6: [0, 3, 7, 9],
  sus2: [0, 2, 7],
  sus4: [0, 5, 7],
  dim: [0, 3, 6],
  aug: [0, 4, 8],
  add9: [0, 4, 7, 14],
};

export interface Chord {
  /** Pitch class of the root, 0-11. */
  readonly root: number;
  readonly intervals: readonly number[];
}

/** "F#m7" -> root and intervals, "-" -> null (no chord), anything else -> throws. */
export function parseChord(symbol: string): Chord | null {
  if (symbol === '-') return null;
  const m = /^([A-G][#b]?)(.*)$/.exec(symbol);
  const root = m ? pitchClass(m[1] ?? '') : null;
  const intervals = m ? QUALITY[m[2] ?? ''] : undefined;
  if (root === null || intervals === undefined) throw new Error(`bad chord "${symbol}"`);
  return { root, intervals };
}

/**
 * A chart ("C | Am | Dm G") -> the chord at every step. Each bar splits evenly among its
 * chords, so a bar may hold 1, 2 or 4 of them.
 */
export function chordSteps(chart: string): (Chord | null)[] {
  const out: (Chord | null)[] = [];
  for (const bar of chart.split('|')) {
    const symbols = bar.trim().split(/\s+/).filter((s) => s !== '');
    if (![1, 2, 4].includes(symbols.length)) throw new Error(`bar "${bar.trim()}" must hold 1, 2 or 4 chords`);
    const each = STEPS_PER_BAR / symbols.length;
    for (const s of symbols) {
      const chord = parseChord(s);
      for (let i = 0; i < each; i++) out.push(chord);
    }
  }
  return out;
}

/** The chord's notes as MIDI numbers, its root placed in the octave starting at `floor`. */
export function voicing(chord: Chord, floor: number): number[] {
  const root = placeAbove(chord.root, floor);
  return chord.intervals.map((i) => root + i);
}
